import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";

import { Button, Typography, useToast } from "@aviary";
import { l } from "@locales/i18n";
import { useBreakpoints, useCallistoPracOnboardingExperiment } from "@shared/hooks";
import type { FileWithPreview } from "@shared/types/fileWithPreview";

import { CheckmarkList } from "../CheckmarkList";
import { Dropzone } from "../Dropzone";

import * as styles from "./CredentialUpload.styles";

const SUPPORTED_FILE_TYPES = "image/jpeg,image/jpg,image/png,application/pdf";

interface Props {
  uploadedFileHandler: (files: File[]) => void;
  isStudent?: boolean;
  capitalizedPatientsTerm?: string;
  treatmentPlanTerm?: string;
  isDisabled?: boolean;
  isEmerson?: boolean;
}

const CredentialUpload = ({
  uploadedFileHandler,
  isStudent,
  capitalizedPatientsTerm,
  treatmentPlanTerm,
  isDisabled,
  isEmerson,
}: Props) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const isDesktop = tablet.greaterThan;
  const isPracOnboardingEnabled = useCallistoPracOnboardingExperiment();
  const { makeToast } = useToast();

  const [selectedFile, setSelectedFile] = useState<FileWithPreview>(null);

  useEffect(() => () => URL.revokeObjectURL(selectedFile?.preview), [selectedFile]);

  const willReviewText = () => {
    if (isStudent) {
      return t(l.credentials.fullscriptWillReview.student);
    }
    if (isEmerson) {
      return t(l.credentials.fullscriptWillReview.emerson);
    }
    if (!isStudent && capitalizedPatientsTerm && treatmentPlanTerm) {
      return t(l.credentials.fullscriptWillReview.dynamicTerm, {
        capitalizedPatientsTerm,
        treatmentPlanTerm,
      });
    }

    return t(l.credentials.fullscriptWillReview.normal);
  };

  const dragAndDropText = () => {
    const shouldChangeText = !isStudent && !isDesktop;

    if (isStudent) return t(l.credentials.dragAndDropStudent);

    if (isPracOnboardingEnabled && shouldChangeText) {
      return t(l.credentials.browseComputer);
    }

    return t(l.credentials.dragAndDrop);
  };

  const renderCheckmarkListItems = () => {
    if (isStudent) {
      return (
        <CheckmarkList>
          <li css={[styles.listItem]}>
            <Typography>{t(l.credentials.ACopyofStudentID)}</Typography>
          </li>
        </CheckmarkList>
      );
    }

    if (isPracOnboardingEnabled) {
      return (
        <CheckmarkList>
          <li>
            <Typography>
              <Trans i18nkey={t(l.credentials.experiments.certUpAddressBack.preferred)}>
                <span>
                  <strong>Preferred:</strong>&nbsp; Healthcare license or registration (with name
                  and expiry)
                </span>
              </Trans>
            </Typography>
          </li>
          <li>
            <Typography>
              <Trans i18nkey={t(l.credentials.experiments.certUpAddressBack.formats)}>
                <span>
                  <strong>Other formats:</strong>&nbsp; Healthcare degree, diploma, certificate,
                  etc.
                </span>
              </Trans>
            </Typography>
          </li>
        </CheckmarkList>
      );
    }

    return (
      <CheckmarkList>
        <li css={[styles.listItem]}>
          <Typography>{t(l.credentials.experiments.certUpAddressBack.license)}</Typography>
        </li>
        <li css={[styles.listItem]}>
          <Typography>{t(l.credentials.experiments.certUpAddressBack.otherFormats)}</Typography>
        </li>
      </CheckmarkList>
    );
  };

  const handleRemove = () => {
    setSelectedFile(null);
    uploadedFileHandler(null);
  };

  const handleUpload = file => {
    setSelectedFile(
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    uploadedFileHandler([file]);
  };

  const handleError = () => {
    makeToast("error", t(l.credentials.rejectedFilesError));
  };

  return (
    <Dropzone
      file={selectedFile}
      supportedFileTypes={SUPPORTED_FILE_TYPES}
      successMessage={{
        heading: t(l.credentials.uploadedSuccessfully),
        description: willReviewText(),
      }}
      onUploadFile={handleUpload}
      onRemoveFile={handleRemove}
      previewUrl={selectedFile?.preview}
      onError={handleError}
    >
      <div css={isPracOnboardingEnabled ? styles.noPaddingReadyContainer : styles.readyContainer}>
        <Typography isSecondaryWeight>{dragAndDropText()}</Typography>
        <div css={styles.checkMark}>{renderCheckmarkListItems()}</div>
        <Button
          isColor={isPracOnboardingEnabled ? "system" : "primary"}
          isOutlined
          disabled={isDisabled}
          isLoading={false}
          tabIndex={0}
          data-e2e="upload-credentials-button"
          data-testid="upload-credentials-button"
        >
          {t(l.credentials.browseComputer)}
        </Button>
      </div>
    </Dropzone>
  );
};

export { CredentialUpload };
